import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBookingsPricingBookingsPricingServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/bookings-pricing',
        destPath: '',
      },
      {
        srcPath: '/_api/bookings-pricing/v2/price',
        destPath: '/v2/pricing',
      },
    ],
    _: [
      {
        srcPath: '/_api/bookings-pricing',
        destPath: '',
      },
      {
        srcPath: '/_api/bookings-pricing/v2/price',
        destPath: '/v2/pricing',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/bookings/v2/pricing/v2/pricing',
        destPath: '/v2/pricing',
      },
      {
        srcPath: '/bookings/v2/pricing',
        destPath: '/v2/pricing',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_pricing';

/**
 * Previews the base price for a set of line items belonging to the same service, before a booking is created.
 *
 * The response includes each line item's individual preview price and the total of all line item preview prices.
 * In most situations, the preview price matches the final booking price when using the same parameters.
 * The final amount may differ due to discounts, booking fees, payment processing charges, or service price changes made by the business between preview and booking creation.
 *
 * Preview Price fails if:
 *
 * - You specify line items that belong to different [services](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction).
 * - The site is using a [Bookings Pricing Integration Service Plugin](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction). Note that external apps can't currently check whether a plugin is installed on the site before calling Preview Price.
 *
 * To retrieve the price of an existing booking, call [Calculate Price](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/calculate-price) instead.
 */
export function previewPrice(payload: object): RequestOptionsFactory<any> {
  function __previewPrice({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'bookingLineItems.pricePerParticipant' },
          { path: 'bookingLineItems.serviceChoices.pricePerParticipant' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.v2.price_info',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.pricing.BookingsPricingService.PreviewPrice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
        protoPath: '/v2/pricing/preview',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'priceInfo.calculatedPrice' },
              { path: 'priceInfo.deposit' },
              { path: 'priceInfo.bookingLineItems.pricePerParticipant' },
              {
                path: 'priceInfo.bookingLineItems.serviceChoices.pricePerParticipant',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __previewPrice;
}

/**
 * Calculates the current base price for an existing booking.
 *
 *
 * Use this method to display booking costs in external apps or dashboards, or to retrieve accurate pricing for bookings created through other channels.
 *
 * The returned price represents the fundamental booking cost before additional charges during [eCommerce checkout](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction). For recently created unpaid bookings, this typically matches the original booking price. However, this method calculates prices based on the service's current pricing configuration, so the returned price may differ from the original booking price if service pricing has changed since the booking was created.
 *
 * ### Price calculation methods
 *
 * __Standard pricing__: Wix Bookings calculates prices based on the number of participants for each [service variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
 * Specify `booking.bookedEntity.slot.serviceId` for appointment-based services and classes, or `booking.bookedEntity.schedule.serviceId` for courses.
 *
 * __Custom pricing__: When a [Pricing Service Provider Plugin](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-integration-service-plugin/introduction) is installed, Calculate Price gets responses directly from the external provider instead of using standard Wix pricing logic.
 * In this case, specify `booking.bookedEntity`. Note that external apps can't currently check whether a plugin is installed on the site before calling Calculate Price.
 *
 * To estimate the price for multiple booking line items before the booking exists, call [Preview Price](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/pricing-api/preview-price) instead.
 */
export function calculatePrice(payload: object): RequestOptionsFactory<any> {
  function __calculatePrice({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'booking.createdDate' },
          { path: 'booking.startDate' },
          { path: 'booking.endDate' },
          { path: 'booking.updatedDate' },
          { path: 'booking.canceledDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'booking.contactDetails.fullAddress.geocode.latitude' },
          { path: 'booking.contactDetails.fullAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.v2.price_info',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.pricing.BookingsPricingService.CalculatePrice',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsPricingBookingsPricingServiceUrl({
        protoPath: '/v2/pricing/calculate',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'priceInfo.calculatedPrice' },
              { path: 'priceInfo.deposit' },
              { path: 'priceInfo.bookingLineItems.pricePerParticipant' },
              {
                path: 'priceInfo.bookingLineItems.serviceChoices.pricePerParticipant',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __calculatePrice;
}
